/**
 * File: track-video.js
 * Description: Tracks YouTube videos from inline <iframe> embeds (static and dynamic).
 */

/** Youtube Tracking */
let ytPlayers = [];
const playerStates = new WeakMap();

window.onYouTubeIframeAPIReady = function () {
  initYouTubePlayers();
  observeForNewIframes();
};

function initYouTubePlayers() {
  const iframes = document.querySelectorAll('iframe[src*="youtube.com/embed"]:not([data-yt-initialized])');

  iframes.forEach((iframe, index) => {
    if (!iframe.src.includes('enablejsapi=1')) {
      try {
        const url = sscmsNewUrl(iframe.src);
        url.searchParams.set('enablejsapi', '1');
        iframe.src = url.toString();
      } catch (err) {
        console.warn('Invalid iframe src URL:', iframe.src);
        return;
      }
    }

    const id = iframe.id || `yt-embed-${Date.now()}-${index}`;
    iframe.id = id;
    iframe.setAttribute('data-yt-initialized', 'true');

    const player = new YT.Player(id, {
      events: {
        'onStateChange': (event) => onPlayerStateChange(event, player, iframe)
      }
    });

    ytPlayers.push(player);
    playerStates.set(player, { lastState: null, hasInteracted: false });
  });
}

function observeForNewIframes() {
  const observer = new MutationObserver(() => {
    if (window.YT && YT.Player) {
      initYouTubePlayers();
    }
  });

  observer.observe(document.body, {
    childList: true,
    subtree: true
  });
}

function onPlayerStateChange(event, player, iframe) {
  const stateCode = event.data;
  let state = interpretPlayerState(stateCode);

  if (!player || state === 'UNKNOWN') return;

  const playerMeta = playerStates.get(player);
  if (!playerMeta) return;

  if (playerMeta.lastState === state) return;

  if (!playerMeta.hasInteracted && state === 'playing') {
    state = 'init';
    playerMeta.hasInteracted = true;
  }

  playerMeta.lastState = state;

  if (['init', 'playing', 'paused', 'ended'].includes(state)) {
    logPlaybackStatus(player, iframe, state);
  }
}

function interpretPlayerState(code) {
  switch (code) {
    case YT.PlayerState.PLAYING: return 'playing';
    case YT.PlayerState.PAUSED: return 'paused';
    case YT.PlayerState.ENDED: return 'ended';
    default: return 'UNKNOWN';
  }
}

function logPlaybackStatus(player, iframe, status) {
  let video_current_time = 0;
  let video_duration = 0;

  try {
    video_current_time = player.getCurrentTime();
    video_duration = player.getDuration();
  } catch (err) {
    console.warn('Unable to get player time/duration:', err);
    return;
  }

  if (status === 'ENDED') {
    video_current_time = video_duration;
  }

  const video_current_time_fixed = video_current_time.toFixed(2);
  const video_duration_fixed = video_duration.toFixed(2);
  const video_percent = (video_duration > 0)
    ? ((video_current_time / video_duration) * 100).toFixed(2)
    : '0.00';

  const video_url = iframe?.src || '';
  const video_title = iframe?.title || 'YouTube Video';

  if (typeof fireAnalyticsEvent === 'function') {
    fireAnalyticsEvent(Track.WebActionTriggered, {
      unique_id: 'video_interaction',
      item_details: {
        video_interaction: {
          video_current_time: video_current_time_fixed,
          video_duration: video_duration_fixed,
          video_percent,
          video_provider: 'youtube',
          video_status: status,
          video_title,
          video_url,
          video_visible: "true"
        }
      }
    });
  }
}

// --- TikTok Tracking ---
function initTikTokTracking() {
  const selectors = [
    '[data-video]:not([data-tt-initialized])',
    '[data-html]:not([data-tt-initialized])'
  ];

  document.querySelectorAll(selectors.join(',')).forEach((element) => {
    element.setAttribute('data-tt-initialized', 'true');

    element.addEventListener('click', () => {
      const rawHtml = element.getAttribute('data-video') || element.getAttribute('data-html');
      if (!rawHtml) return;

      const parsed = safelyParseTikTokHTML(rawHtml);
      if (!parsed) return;

      const { videoUrl, title } = parsed;

      if (typeof fireAnalyticsEvent === 'function') {
        fireAnalyticsEvent(Track.WebActionTriggered, {
          unique_id: 'video_interaction',
          item_details: {
            video_interaction: {
              video_current_time: "n/a",
              video_duration: "n/a",
              video_percent: "n/a",
              video_provider: "tiktok",
              video_status: "init",
              video_title: title,
              video_url: videoUrl,
              video_visible: "true"
            }
          }
        });
      }
    });
  });
}

function safelyParseTikTokHTML(rawHtml) {
  try {
    const decoded = decodeEntities(rawHtml);

    const videoUrlMatch = decoded.match(/cite="([^"]+)"/);
    const videoUrl = videoUrlMatch ? videoUrlMatch[1] : null;

    const pMatch = decoded.match(/<p[^>]*>([\s\S]*?)<\/p>/);
    let title = pMatch ? pMatch[1] : null;

    if (title) {
      title = stripTagsBasic(title);
      title = escapeHtml(title);
    }

    if (!videoUrl || !title) return null;

    return { videoUrl, title };
  } catch (err) {
    console.warn('Failed to parse TikTok embed:', err);
    return null;
  }
}

function decodeEntities(html) {
  const textarea = document.createElement('textarea');
  textarea.textContent = html;
  return textarea.value;
}

function stripTagsBasic(input) {
  return input.replace(/<\/?[^>]+(>|$)/g, '');
}

function escapeHtml(str) {
  return str
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function observeForNewTikTokEmbeds() {
  const observer = new MutationObserver(() => {
    initTikTokTracking();
  });

  observer.observe(document.body, {
    childList: true,
    subtree: true
  });
}

document.addEventListener('DOMContentLoaded', () => {
  initTikTokTracking();
  observeForNewTikTokEmbeds();
});